import { Entity, Id, Paging, Context, Order, PaginatedResult, RealDateTimeProvider } from '../../../common'
import { ROTransaction, RWTransaction } from '../../../db'
import { CardStatus } from '../../identityCards'
import { AddCitizenDocument, CitizenDocumentInfo, CitizenDocument } from '../../citizenDocuments'

export enum CitizenSex {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export function citizenSexName(sex?: CitizenSex): string {
  switch (sex) {
    case CitizenSex.FEMALE:
      return 'Женский'
    case CitizenSex.MALE:
      return 'Мужской'
    default:
      return '-'
  }
}

export enum CitizenCategory {
  CH = 'CH',
  AB_M = 'AB_M',
  AB_F = 'AB_F',
  P_M = 'P_M',
  P_F = 'P_F',
}

export function calcCitizenCategory(citizenSex: CitizenSex, citizenBirthdate: Date): CitizenCategory {
  const now = new RealDateTimeProvider().get()

  let fullAge = now.getFullYear() - citizenBirthdate.getFullYear()
  let monthDiff = now.getMonth() - citizenBirthdate.getMonth()
  const dateDiff = now.getDate() - citizenBirthdate.getDate()
  if (dateDiff < 0) {
    monthDiff -= 1
  }
  if (monthDiff < 0) {
    fullAge -= 1
  }

  if (fullAge < 16) {
    return CitizenCategory.CH
  }
  switch (citizenSex) {
    case CitizenSex.MALE:
      if (fullAge < 65) {
        return CitizenCategory.AB_M
      } else {
        return CitizenCategory.P_M
      }
    case CitizenSex.FEMALE:
      if (fullAge < 60) {
        return CitizenCategory.AB_F
      } else {
        return CitizenCategory.P_F
      }
  }
}

export function citizenCategoryName(category?: CitizenCategory): string {
  if (category === undefined) return '-'

  switch (category) {
    case CitizenCategory.CH:
      return 'Дети (до 15 лет)'
    case CitizenCategory.AB_M:
      return 'Трудоспособные мужчины (16-65 лет)'
    case CitizenCategory.AB_F:
      return 'Трудоспособные женщины (16-60 лет)'
    case CitizenCategory.P_M:
      return 'Пенсионеры мужчины (старше 65 лет)'
    case CitizenCategory.P_F:
      return 'Пенсионеры женщины (старше 60 лет)'
  }
}
export function citizenCategoryShorthand(category?: CitizenCategory): string {
  if (category === undefined) return '-'

  switch (category) {
    case CitizenCategory.CH:
      return 'Д'
    case CitizenCategory.AB_M:
      return 'ТС_М'
    case CitizenCategory.AB_F:
      return 'ТС_Ж'
    case CitizenCategory.P_M:
      return 'П_М'
    case CitizenCategory.P_F:
      return 'П_Ж'
  }
}
export interface Citizen extends Entity {
  surname: string
  name: string
  patronymic?: string
  sex: CitizenSex
  birthdate: Date
  phone?: string
  municipalEntityId: Id
  locality: string
  street: string
  house: string
  appartment?: string
  category: CitizenCategory
  guardianIds?: Id[]
  dependentIds?: Id[]
}

export interface CitizenFullInfo {
  citizen: Citizen
  document: CitizenDocumentInfo
  cardStatus?: CardStatus
  municipalEntity: string
}

export interface CitizenFilter {
  ids?: Id[]
  /**
   * Full text search by fullname, phone, document, address
   */
  search?: string
  municipalEntityId?: Id
  cardStatus?: CardStatus
  month?: number
  year?: number
}

export enum CitizenOrderFields {
  FULLNAME = 'fullname',
  CREATED_AT = 'createdAt',
}

export interface CitizenFindOptions {
  filter?: CitizenFilter
  order?: Order<CitizenOrderFields> | undefined
  paging?: Paging
}

export function citizenGetFullname(citizen: Citizen) {
  return [citizen.surname, citizen.name, citizen.patronymic].filter(val => val).join(' ')
}

export function citizenGetAddress(citizen: Citizen) {
  return [citizen.locality, citizen.street, citizen.house, citizen.appartment].filter(val => val).join(' ')
}

export type AddCitizenOpts = {
  surname: string
  name: string
  patronymic?: string
  sex: CitizenSex
  birthdate: Date
  phone?: string
  municipalEntityId: Id
  locality: string
  street: string
  house: string
  appartment?: string
  category: CitizenCategory
  documents: AddCitizenDocument[]
}

export type UpdateCitizenOpts = {
  surname?: string
  name?: string
  patronymic?: string
  sex?: CitizenSex
  birthdate?: Date
  phone?: string
  municipalEntityId?: Id
  locality?: string
  street?: string
  house?: string
  appartment?: string
  category?: CitizenCategory
  documents?: AddCitizenDocument[]
  guardianIds?: Id[]
  dependentIds?: Id[]
}

export interface CitizensRepository {
  findAll(tx: ROTransaction, opts?: CitizenFindOptions): Promise<PaginatedResult<Citizen>>
  findById(tx: ROTransaction, id: Id): Promise<Citizen | undefined>
  findByIdNonDeleted(tx: ROTransaction, id: Id): Promise<Citizen | undefined>
  save(tx: RWTransaction, citizen: Citizen): Promise<void>
  delete(tx: RWTransaction, id: Id, deletedAt: Date): Promise<void>
}

export interface CitizensService {
  findAll(ctx: Context, opts?: CitizenFindOptions): Promise<PaginatedResult<Citizen>>
  findAllWithInfo(ctx: Context, opts?: CitizenFindOptions): Promise<PaginatedResult<CitizenFullInfo>>
  findById(ctx: Context, id: Id): Promise<Citizen | undefined>
  findCitizenDocuments(ctx: Context, citizenId: Id): Promise<CitizenDocument[]>
  add(ctx: Context, citizen: AddCitizenOpts): Promise<Id>
  addDependent(ctx: Context, dependentId: Id, guardianId: Id): Promise<void>
  removeDependent(ctx: Context, dependentId: Id, guardianId: Id): Promise<void>
  update(ctx: Context, id: Id, opts: UpdateCitizenOpts): Promise<void>
  delete(ctx: Context, id: Id): Promise<void>
}
